import { useEffect, useRef } from 'react'
import { useCockpitStore } from '@/stores/cockpit-store'
import { useGeolocationStore } from '@/stores/geolocation-store'
import { useSelectionStore } from '@/stores/selection-store'
import { useTimelineStore } from '@/stores/timeline-store'

/**
 * Bridges legacy single-select stores (cockpit, geolocation) with the new
 * cross-view `selection-store`. Mounted once at the case-layout level so any
 * view's existing select wiring automatically reflects into the canonical
 * cross-view selection.
 *
 * Renders nothing — this is a side-effect-only component.
 *
 * Loop prevention: each direction guards on a `previous` ref of its OWN
 * side's value and bails when the dep change came purely from the other
 * side. The previous "skipNext token" approach broke when two bridges
 * shared the same store — bridge A's write would fire bridge B's effect,
 * bridge B would then try to "sync" its own (unchanged) side back, which
 * cleared bridge A's just-applied selection, kicking off an infinite loop
 * (https://stack: clear → selection-store setState → … → Maximum update
 * depth exceeded).
 */
export function SelectionBridge() {
  return (
    <>
      <CockpitBridge />
      <GeolocationBridge />
    </>
  )
}

function CockpitBridge() {
  const cockpitSelected = useCockpitStore(s => s.selectedNodeId)
  const setCockpitSelected = useCockpitStore(s => s.setSelectedNodeId)
  const storeSelected = useSelectionStore(s => firstId(s.selectedEntityIds))
  const replace = useSelectionStore(s => s.replace)
  const clear = useSelectionStore(s => s.clear)

  // Track the last value we *observed* on each side. When an effect fires
  // and its OWN side hasn't changed since last observation, the change came
  // from somewhere else — we have nothing to mirror in that direction.
  const lastCockpit = useRef<string | null | undefined>(undefined)
  const lastStore = useRef<string | null | undefined>(undefined)

  // cockpit → store
  useEffect(() => {
    if (lastCockpit.current === cockpitSelected) return
    lastCockpit.current = cockpitSelected
    if (cockpitSelected === storeSelected) return
    if (cockpitSelected) replace([cockpitSelected], 'graph')
    else clear('graph')
  }, [cockpitSelected, storeSelected, replace, clear])

  // store → cockpit
  useEffect(() => {
    if (lastStore.current === storeSelected) return
    lastStore.current = storeSelected
    if (storeSelected === cockpitSelected) return
    setCockpitSelected(storeSelected)
  }, [storeSelected, cockpitSelected, setCockpitSelected])

  return null
}

function GeolocationBridge() {
  const geoSelected = useGeolocationStore(s => s.selectedEntityId)
  const selectEntity = useGeolocationStore(s => s.selectEntity)
  const entityKeyToNodeId = useTimelineStore(s => s.entityKeyToNodeId)
  const nodeIdToKey = useTimelineStore(s => s.nodeIdToEntityKey)
  const storeSelected = useSelectionStore(s => firstId(s.selectedEntityIds))
  const replace = useSelectionStore(s => s.replace)
  const clear = useSelectionStore(s => s.clear)

  const lastGeo = useRef<string | null | undefined>(undefined)
  const lastStore = useRef<string | null | undefined>(undefined)

  // map (geo entity key) → store (graph node id)
  useEffect(() => {
    if (lastGeo.current === geoSelected) return
    lastGeo.current = geoSelected
    const mappedToNode = geoSelected ? (entityKeyToNodeId[geoSelected] ?? null) : null
    if (mappedToNode === storeSelected) return
    if (mappedToNode) replace([mappedToNode], 'map')
    else if (!geoSelected) clear('map')
  }, [geoSelected, entityKeyToNodeId, storeSelected, replace, clear])

  // store (graph node id) → map (geo entity key)
  useEffect(() => {
    if (lastStore.current === storeSelected) return
    lastStore.current = storeSelected
    const mappedToKey = storeSelected ? (nodeIdToKey[storeSelected] ?? null) : null
    if (mappedToKey === geoSelected) return
    // The map's pick changes here by the bridge's own hand, so the other
    // direction has nothing to mirror back. A node the map does not show maps
    // to no key: read as the user deselecting on the map, that empty pick
    // cleared the selection that had just been made.
    lastGeo.current = mappedToKey
    selectEntity(mappedToKey)
  }, [storeSelected, nodeIdToKey, geoSelected, selectEntity])

  return null
}

function firstId(set: ReadonlySet<string>): string | null {
  if (set.size === 0) return null
  return set.values().next().value ?? null
}
